import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import constants from "../../Utils/constants";
import docsData from "../../Utils/docsData";
import {
  getDocumentsFromEmploymentType,
  haveValue,
  toast,
  getRequiredPayslipPeriods,
  getPayslipDateWarning,
} from "../../helper/common";

import DocItem from "../../components/Onboarding/DocItem";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import ApprovedModal from "./approvedModal";
import DocumentAnalysisLoadingPopup from "../../components/Onboarding/DocumentAnalysisLoading";
import { docsMap } from "../../Utils/skorLiteDocumentMap";
import { useNavigate } from "react-router-dom";
import Cancelimg from "../../assets/img/cancel-01.svg";
import PermissoWidget from "./PermisssionWidget";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { trackClarityEvent } from "../../Utils/clarityReady";
import AuthContext from "../../context/auth/AuthContext";
import ButtonActionTooltip from "../../components/ErrorModal/ButtonActionTooltip";
import DisableZoom from "../../helper/disableZoom";
import styles from "./RequiredDocumentsModal.module.css";
import {
  isKycReviewFile as isKycReviewFileUtil,
  isKycTenantDoc,
} from "../../Utils/kycUtils";
import moreInfoimg from "../../assets/img/moreInfoimg.svg";
import ClaveModal from "../../components/ClaveModal/ClaveModal";

const PAYSLIP_PERIOD_KEYS = [
  constants.firstMonthPayslip,
  constants.secondMonthPayslip,
  constants.thirdMonthPayslip,
];

const REVIEW_TAGS = {
  scan: "fileReviewTagScan",
  duplicate: "fileReviewTagDuplicate",
  thirdParty: "fileReviewTagThirdParty",
};

const getFileNameFromUrl = (url) => {
  if (!haveValue(url) || typeof url !== "string") return "";

  try {
    return new URL(url).pathname.split("/").filter(Boolean).pop() ?? "";
  } catch {
    return url.split("?")[0].split("/").filter(Boolean).pop() ?? "";
  }
};

const getProfileDocuments = (profile) => {
  const buckets = profile?.report?.documentBuckets ?? [];

  if (buckets.length > 0) {
    return buckets.flatMap((bucket) =>
      (bucket?.documents ?? []).map((doc) => ({
        ...doc,
        bucketName: bucket?.bucketName ?? doc?.bucketName,
      }))
    );
  }

  return profile?.report?.allDocuments ?? [];
};

const buildFilesToReview = (documentAnalysisResponse, user) => {
  const profiles =
    documentAnalysisResponse?.document_analysis?.profileReports ??
    documentAnalysisResponse?.profileReports ??
    [];
  const forensicReports =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const scannedFileNames = new Set();

  forensicReports.forEach((entry) => {
    (entry?.report?.documents_list ?? []).forEach((doc) => {
      if (
        doc?.documentType === "is_scanned_document" ||
        doc?.isScannedDocument === "yes"
      ) {
        scannedFileNames.add(getFileNameFromUrl(doc?.url));
      }
    });
  });

  return profiles.flatMap((profile) => {
    const isUploader = profile?.data?.is_uploader;
    const ownerName = profile?.data?.owner || "unknown";
    const ownerType = profile?.data?.owner_type || profile?.owner_type;

    return getProfileDocuments(profile).flatMap((doc) => {
      const url = doc?.data?.url ?? doc?.url;
      const fileName = getFileNameFromUrl(url) || doc?.documentName;
      let tag = null;

      if (doc?.isDuplicate) tag = "duplicate";
      else if (!isUploader || ownerType === "legal_entity") tag = "thirdParty";
      else if (scannedFileNames.has(fileName)) tag = "scan";

      return fileName && tag
        ? [
            {
              id: `${ownerName}-${fileName}-${doc?.documentId ?? doc?.url}`,
              fileName,
              tag,
              ownerName,
              documentName: doc?.documentName,
            },
          ]
        : [];
    });
  });
};

const RequiredDocumentsModal = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  isCoTenant = false,
  isFirstTime = false,
  guarantor_a_id,
  co_tenant_a_id,
  reqFor,
  onClose,
  redirectUrl,
  setDocAnalysisLoading,
  isOpen,
  reloadTrigger,
  needWaitingJobForClave,
  isEditMode,
  fetchFileDataParent,
  modules,
  handleSubmitFinalStep,
  isDummySignup,
  setStep,
  isFormSubmitted,
  setIsFormSubmitted,
  documentAnalysisResponse,
  hideClave = false,
  llCreated = false,
  cameFromMissing = false,
  requiresSubcategory,
}) => {
  const requiresSubcategoryFn =
    requiresSubcategory ??
    ((s) =>
      [
        constants.public_servant,
        constants.employed,
        constants.self_employed,
      ].includes(s));
  const isKycDone = user?.onfido_user_kyc?.server_status === "completed";
  const fileInputRef = useRef(null);
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    initiateBankFlip,
    fetchTenantDocs,
    uploadFileData,
    fetchWaitingData,
    detectDocument,
    skipDuplicateDocument,
    deleteDuplicateDocuments,
    deleteDocument,
  } = useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [docUploadLoading, setDocUploadLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showApprovalPopup, setShowApprovalPopup] = useState(false);
  const [availableClaveDocs, setAvailableClaveDocs] = useState([]);
  const [tenantDocsData, setTenantDocsData] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(true);
  const [waitingJobLoading, setWaitingJobLoading] = useState(false);
  const queryParameters = new URLSearchParams(window.location.search);
  const waitingId = queryParameters.get("waiting");
  const [waitingJobId, setWaitingJobId] = useState(null);
  // got from clave as a redirect url
  const welcome = queryParameters.get("welcome");
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [claveWidgetUrl, setClaveWidgetUrl] = useState(null);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [deletedReviewFileIds, setDeletedReviewFileIds] = useState([]);
  const [showClaveModal, setShowClaveModal] = useState(false);

  const handleClaveNext = () => {
    setShowClaveModal(false);
  };

  useEffect(() => {
    setShowWelcomePopup(welcome === "true");
  }, [queryParameters]);

  // ✅ File Validation Helper
  const validateFiles = async (newFiles) => {
    const validatedFiles = [];
    const invalidFilesTemp = [];

    for (let file of newFiles) {
      // 🔹 File type check
      if (
        !["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(
          file.type
        )
      ) {
        invalidFilesTemp.push({
          name: file.name,
          reason: _t("fileTypeNotAllowed", "File type not allowed"),
        });
        continue;
      }

      // 🔹 File size check (8 MB)
      if (file.size > 8 * 1024 * 1024) {
        invalidFilesTemp.push({
          name: file.name,
          reason: _t(
            "fileUploadedExceedsTwoPointFiveMbFileLimit",
            "File exceeds 4MB limit"
          ),
        });
        continue;
      }

      try {
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } else {
          // 🔹 Validate image
          await new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error("Corrupted image"));
            };
            img.src = objectUrl;
          });
        }

        validatedFiles.push(file);
      } catch (err) {
        let reason = _t(
          "isCorruptedOrUnreadableFile",
          "is corrupted or unreadable"
        );
        if (err?.message?.toLowerCase()?.includes("password")) {
          reason = _t("isPasswordProtectedPDF", "is password protected");
        }

        invalidFilesTemp.push({ name: file.name, reason });
      }
    }

    // ✅ Append invalid files instead of replacing
    if (invalidFilesTemp.length > 0) {
      setInvalidFiles((prev) => [...prev, ...invalidFilesTemp]);
    }

    // ✅ Return validated files so you can append them in parent logic
    return validatedFiles;
  };

  const startWaitingJobInterval = (waitingId) => {
    if (!haveValue(waitingId)) return;

    setWaitingJobLoading(true);

    const temp_interval = setInterval(() => {
      fetchWaitingData(waitingId)
        .then((res) => {
          if (res.status === constants.COMPLETED) {
            clearInterval(temp_interval);

            // Doing this for tenant for approval popup to get latest clave done value.
            if (!isGuarantor && !isCoTenant) {
              loadUserTenant("tenant");
            }
            fetchFileData(res?.tenant_id);
            fetchFileDataParent?.();
            setWaitingJobLoading(false);

            // Remove 'waiting' param from URL
            const params = new URLSearchParams(window.location.search);
            params.delete("waiting");
            const newUrl =
              window.location.pathname +
              (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", newUrl);
          }
        })
        .catch((err) => {
          console.error("❌ Waiting job error:", err);
          setWaitingJobLoading(false);
          clearInterval(temp_interval);
        });
    }, 1000);

    return temp_interval;
  };

  useEffect(() => {
    if (haveValue(waitingId)) {
      const intervalId = startWaitingJobInterval(waitingId);

      return () => clearInterval(intervalId);
    }
  }, []);

  useEffect(() => {
    fetchFileData();
  }, [reloadTrigger, isOpen]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleUploadFiles = async (
    fileType = "uploaded-from-onboarding",
    fileFrom
  ) => {
    if (docFound.size === 0 && selectedFiles.length === 0) {
      toast.error(
        _t("pleaseUploadDocumentsToContinue", "Text used in onboarding flow")
      );
      return;
    }
    if (user?.re_new_pending && selectedFiles.length === 0) {
      toast.error(
        _t("pleaseUploadDocumentsToContinue", "Text used in onboarding flow")
      );
      return;
    }
    try {
      if (reqFor === "updateDoc") {
        if (selectedFiles.length > 0) {
          setDocAnalysisLoading(true);
        } else {
          onClose();
        }
      } else {
        setDocUploadLoading(true);
      }
      await skipDuplicateDocument();
      const formData = new FormData();

      for (let file of selectedFiles) {
        formData.append("tenantDocs", file);
      }

      formData.append("fileType", fileType);
      if (fileFrom) formData.append("fileFrom", fileFrom);

      if (selectedFiles.length > 0) {
        const uploadResponse = await uploadFileData(
          formData,
          llCreated,
          user?._id,
          isGuarantor || isCoTenant
        );

        if (
          uploadResponse?.status !== 200 ||
          uploadResponse?.data?.success !== true
        ) {
          toast.error(_t("errorInUploadingFile"));
          if (reqFor === "updateDoc") {
            setDocAnalysisLoading(false);
          } else {
            setDocUploadLoading(false);
          }
          return;
        }

        setSelectedFiles([]);
        if (reqFor === "updateDoc") {
          setDocAnalysisLoading(false);
          onClose();
          fetchFileDataParent?.();
          // If we are waiting for documents to upload
        } else {
          // Hand off analysis loading to MissingDocument (step 4) — avoid
          // showing two DocumentAnalysisLoadingPopup at once (RequiredDocuments
          // and MissingDocument both render one).
          setDocUploadLoading(false);
          // removing clavedone field
          const url = new URL(window.location);
          url.searchParams.delete("claveDone");
          window.history.replaceState(
            {},
            document.title,
            url.pathname + url.search
          );
          // removing clavedone field
          if (isEditMode) {
            formHandler?.next?.();
          } else {
            const role = localStorage.getItem("usertype");
            let step = 4;
            if (Array.isArray(modules)) {
              if (
                modules.includes("DISABLE_KYC") &&
                modules.includes("DISABLE_BANK") &&
                modules.includes("DISABLE_GUARANTORS")
              ) {
                step = -1;
              } else if (
                modules.includes("DISABLE_KYC") &&
                modules.includes("DISABLE_BANK")
              ) {
                step = isGuarantor ? -1 : 7;
              } else if (modules.includes("DISABLE_KYC")) {
                step = 6;
              }
            }
            if (step === -1 && isGuarantor) {
              handleSubmitFinalStep();
            } else {
              let payload = { step: isGuarantor ? 6 : 4, role };
              if (modules?.includes("DISABLE_KYC")) {
                payload.manual_kyc_doc_status = "BYPASS";
              }
              await updateUserProfile(payload);
              if (window.location.pathname === "/onboarding" && !isGuarantor) {
                trackClarityEvent("tenant_document_upload_step_ended");
                setTimeout(() => {
                  trackClarityEvent("tenant_missing_document_step_started");
                }, constants.CLARITY_DELAY);
              }
            }
          }
        }
      } else {
        // No new files — still need to clear any upload loader before
        // advancing, so only MissingDocument's analysis modal shows.
        setDocUploadLoading(false);
        const role = localStorage.getItem("usertype");
        const url = new URL(window.location);
        detectDocument(user?._id);
        // removing clavedone field
        url.searchParams.delete("claveDone");
        window.history.replaceState(
          {},
          document.title,
          url.pathname + url.search
        );
        // removing clavedone field
        if (isEditMode) {
          formHandler?.next?.();
        } else {
          let step = 4;
          if (Array.isArray(modules)) {
            if (
              modules.includes("DISABLE_KYC") &&
              modules.includes("DISABLE_BANK") &&
              modules.includes("DISABLE_GUARANTORS")
            ) {
              step = -1;
            } else if (
              modules.includes("DISABLE_KYC") &&
              modules.includes("DISABLE_BANK")
            ) {
              step = isGuarantor ? -1 : 7;
            } else if (modules.includes("DISABLE_KYC")) {
              step = 6;
            }
          }
          if (step === -1 && isGuarantor) {
            handleSubmitFinalStep();
          } else {
            let payload = { step: isGuarantor ? 6 : 4, role };
            if (modules?.includes("DISABLE_KYC")) {
              payload.manual_kyc_doc_status = "BYPASS";
            }
            await updateUserProfile(payload);
            if (window.location.pathname === "/onboarding" && !isGuarantor) {
              trackClarityEvent("tenant_document_upload_step_ended");
              setTimeout(() => {
                trackClarityEvent("tenant_missing_document_step_started");
              }, constants.CLARITY_DELAY);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error uploading multiple files:", err);
      if (reqFor === "updateDoc") {
        setDocAnalysisLoading(false);
      } else {
        setDocUploadLoading(false);
      }
    }
  };

  const processFiles = async (newFiles) => {
    if (!Array.isArray(newFiles)) return;

    const validFiles = await validateFiles(newFiles);
    if (validFiles.length === 0) return;

    // Total size check (existing + new)
    const existingSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const newSize = validFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSize = existingSize + newSize;

    if (totalSize > 20 * 1024 * 1024) {
      toast.error(_t("totalFileSizeExceeds20MB"));
      return;
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
    e.target.value = "";
  };

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    processFiles(newFiles);
  };

  const fetchFileData = async (custom_tenant_id) => {
    setDocumentLoading(true);
    try {
      const tenant_id = haveValue(custom_tenant_id)
        ? custom_tenant_id
        : user?._id;
      const res = await fetchTenantDocs(
        tenant_id,
        llCreated,
        isGuarantor || isCoTenant
      );
      const rawDocs = res?.data?.success_res?.tenantDocs ?? [];
      const filteredDocs = Array.isArray(rawDocs)
        ? rawDocs.filter((doc) => !isKycTenantDoc(doc))
        : [];
      setTenantDocsData(filteredDocs);
    } catch (error) {
      console.error("Error fetching tenant docs:", error);
    } finally {
      setDocumentLoading(false);
    }
  };

  const getRequiredProtectedHousingDocs = () => {
    const selectedProtectedDocs =
      user?.tenant_extra_field_id?.[constants.public_housing_fileFrom] ?? [];

    const baseProtectedDocs = selectedProtectedDocs.filter(
      (doc) =>
        doc &&
        doc !== constants.no_special_circumstances &&
        doc !== "no" &&
        doc?.toLowerCase?.() !== "no special circumstances" &&
        doc !== constants.certificate_of_residency_registration
    );

    const hasNoSpecialCircumstances =
      selectedProtectedDocs.includes(constants.no_special_circumstances) ||
      selectedProtectedDocs.includes("no") ||
      selectedProtectedDocs.some(
        (v) => v?.toLowerCase?.() === "no special circumstances"
      );

    if (baseProtectedDocs.length > 0) {
      return [
        ...baseProtectedDocs,
        constants.certificate_of_residency_registration,
      ];
    }

    if (hasNoSpecialCircumstances) {
      return [constants.certificate_of_residency_registration];
    }

    return [];
  };

  const [otherDocs, setOtherDocs] = useState([]);
  const [payslipDocs, setPayslipDocs] = useState([]);
  const [docFound, setDocFound] = useState(new Map());
  const [haveClavePinDoc, setHaveClavePinDoc] = useState(false);
  const [payslipValidation, setPayslipValidation] = useState(new Map());
  const [requiredPayslipPeriods, setRequiredPayslipPeriods] = useState([]);
  const isThreePayslipRequired = useMemo(
    () =>
      user?.employment_status === constants.employed ||
      user?.employment_status === constants.public_servant,
    [user]
  );

  useEffect(() => {
    if (isThreePayslipRequired) {
      const periods = getRequiredPayslipPeriods();
      setRequiredPayslipPeriods(periods);
    } else {
      setRequiredPayslipPeriods([]);
    }
  }, [isThreePayslipRequired]);

  useEffect(() => {
    if (haveValue(tenantDocsData) && tenantDocsData.length >= 1) {
      const temp_map = new Map();
      const type_map = new Map();
      const other_type = new Map();
      let payslips = [];
      const payslipValidationMap = new Map();

      const matchedDocuments =
        documentAnalysisResponse?.document_analysis?.profileReports?.flatMap(
          (r) => r.report?.matchedDocuments
        ) ?? [];

      const documentPeriodMap = new Map();
      for (const doc of matchedDocuments) {
        if (doc?.data?.url && doc?.period_year && doc?.period_month) {
          const urlParts = doc.data.url.split("/");
          const filename = urlParts[urlParts.length - 1];
          documentPeriodMap.set(filename, {
            period_year: doc.period_year,
            period_month: doc.period_month,
          });
        }
      }

      const matchedPeriodIndexes = new Set();
      const reviewFileNames = new Set(
        buildFilesToReview(documentAnalysisResponse, user).map(
          (file) => file.fileName
        )
      );

      tenantDocsData.map((docs) => {
        if (isKycTenantDoc(docs)) return;
        if (docs?.from !== "clave" && reviewFileNames.has(docs?.file_name)) {
          return;
        }

        if (docs?.type?.toLowerCase()?.includes("payslip")) {
          let docWithPeriod = docs;
          if (documentPeriodMap.size > 0) {
            const periodInfo = documentPeriodMap.get(docs.file_name);
            if (periodInfo) {
              docWithPeriod = {
                ...docs,
                period_year: periodInfo.period_year,
                period_month: periodInfo.period_month,
              };
            }
          }

          if (isThreePayslipRequired) {
            const payslipPeriodYear = docWithPeriod?.period_year;
            const payslipPeriodMonth = docWithPeriod?.period_month;
            let matchedPeriodIndex = -1;

            if (
              payslipPeriodYear &&
              payslipPeriodMonth &&
              requiredPayslipPeriods.length > 0
            ) {
              matchedPeriodIndex = requiredPayslipPeriods.findIndex(
                (period) =>
                  period.period_year === payslipPeriodYear &&
                  period.period_month === payslipPeriodMonth
              );
            }

            if (
              matchedPeriodIndex >= 0 &&
              !matchedPeriodIndexes.has(matchedPeriodIndex)
            ) {
              matchedPeriodIndexes.add(matchedPeriodIndex);
              type_map.set(
                PAYSLIP_PERIOD_KEYS[matchedPeriodIndex],
                docWithPeriod
              );
            } else {
              payslips.push(docWithPeriod);
              if (documentPeriodMap.size > 0) {
                const warning = getPayslipDateWarning(
                  docWithPeriod,
                  requiredPayslipPeriods,
                  _t
                );
                if (warning) {
                  payslipValidationMap.set(
                    docs?._id || docs?.file_name,
                    warning
                  );
                }
              }
            }
          } else {
            payslips.push(docWithPeriod);
          }
          return;
        }
        if (docs?.from == "clave") {
          temp_map.set(docs?.type);
          setHaveClavePinDoc(true);
        }
        if (
          !type_map.has(docs?.type + (docs?.year ? docs?.year : "")) &&
          docs?.from != constants.others
        ) {
          type_map.set(docs?.type + (docs?.year ? docs?.year : ""), docs);
        }
        if (docs?.from == "others" && !other_type?.has(docs?.type)) {
          other_type.set(docs?.type, docs);
        }
      });

      payslips = payslips.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setPayslipDocs(payslips);
      setDocFound(type_map);
      setOtherDocs([...other_type.values()]);
      setPayslipValidation(payslipValidationMap);
    } else {
      setPayslipDocs([]);
      setDocFound(new Map());
      setOtherDocs([]);
      setPayslipValidation(new Map());
    }
  }, [
    tenantDocsData,
    requiredPayslipPeriods,
    documentAnalysisResponse,
    isThreePayslipRequired,
    user?._id,
  ]);

  const [documentList, setDocumentList] = useState([]);
  const [selectedDocData, setSelectedDocData] = useState({});
  const [additionalDocs, setAdditionalDocs] = useState([]);

  useEffect(() => {
    const notInDocumentList = Array.from(docFound.entries())
      .filter(([key]) => !documentList.includes(key))
      .map(([_, value]) => value);
    setAdditionalDocs(notInDocumentList);
  }, [docFound, documentList]);

  useEffect(() => {
    if (!user) return;

    let allDocs = getDocumentsFromEmploymentType(user, true);
    setSelectedDocData(allDocs);

    let allDocsWithoutProtectedHousing = getDocumentsFromEmploymentType(user);
    let baseDocs = Object.keys(allDocsWithoutProtectedHousing);
    const finalProtectedDocs = getRequiredProtectedHousingDocs();

    const mergedDocs = [
      ...new Set([...baseDocs, ...finalProtectedDocs]),
    ].filter(
      (key) =>
        haveValue(allDocs[key]) || haveValue(docsData.PROTECTED_HOUSING[key])
    );

    setDocumentList(mergedDocs);
  }, [user]);

  useEffect(() => {
    // If we are in dummy signup flow show a static list of documents
    // instead of computing document list from employment type.
    if (isDummySignup) {
      const staticDocs = [
        "Deduction certificate",
        "Working life certificate",
        "Last annual Tax Report (Model 100)",
        "Payslip",
        "Employment contract",
      ];
      // selectedDocData is expected elsewhere; set a minimal mapping so components
      // that look up selectedDocData[docKey] won't break. We'll keep keys as the
      // display names and let DocItem use translationFromFixed when rendering.
      const mapping = {};
      staticDocs.forEach((d) => (mapping[d] = d));
      setSelectedDocData(mapping);
      setDocumentList(staticDocs);
      return;
    }
  }, []);

  const handleClave = () => {
    if (isDummySignup) {
      setShowClaveModal(true);
    } else {
      handleInitiateClave();
    }
  };

  // ********** clave functionality **********
  const handleInitiateClave = async (e) => {
    setWaitingJobId(null);

    let temp_clave_models = {
      AEAT_100_RENTA: false,
      AEAT_130_IRPF_DIRECTA: false,
      AEAT_303_IVA_AUTOLIQUIDACION: false,
      SEG_SOCIAL_VIDA_LABORAL: false,
      SEG_SOCIAL_CERT_INTEGRAL_PRESTACIONES: false,
      SEG_SOCIAL_BASE_COTIZACIONES: false,
    };

    if (user?.employment_status == constants.employed) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }
    if (
      user?.employment_status == constants.employed ||
      user?.employment_status == constants.unemployed ||
      user?.employment_status == constants.public_servant
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_VIDA_LABORAL: true,
        AEAT_100_RENTA: true,
      };
    }
    if (user?.employment_status == constants.self_employed) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_VIDA_LABORAL: true,
        AEAT_100_RENTA: true,
        AEAT_130_IRPF_DIRECTA: true,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }
    if (
      user?.employment_status == constants.self_employed &&
      user?.employment_category == "freelancer"
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        AEAT_303_IVA_AUTOLIQUIDACION: true,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }

    if (
      user?.employment_status == constants.retired ||
      user?.employment_status == constants.unemployed
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_CERT_INTEGRAL_PRESTACIONES: true,
      };
    }

    const coTenantRedirect = co_tenant_a_id
      ? `co-tenant/onboarding?co_tenant_a_id=${co_tenant_a_id}&claveDone=true`
      : "co-tenant/onboarding?claveDone=true";
    initiateBankFlip(
      temp_clave_models,
      false,
      user?._id,
      reqFor === "updateDoc"
        ? redirectUrl
        : isGuarantor
          ? `guarantor/onboarding?guarantor_a_id=${guarantor_a_id}&claveDone=true`
          : isCoTenant
            ? coTenantRedirect
            : "onboarding?claveDone=true&waiting=",
      needWaitingJobForClave
    )
      .then((res) => {
        setClaveWidgetUrl(res.success_res.widgetLink);
        setWaitingJobId(res.success_res.waiting_id);
      })
      .catch((err) => {
        toast.error(_t("errorWhileInitiatingTheSignatureProcess"));
        trackClarityEvent("tenant_bankflip_error");
      });
  };

  const handleClose = () => {
    setClaveWidgetUrl(null);
  };

  const handleSuccess = (data) => {
    startWaitingJobInterval(waitingJobId);
    toast.success(_t("bankflipSuccess"));
  };

  const handleError = (data) => {
    toast.success(
      _t("bankflipFailure", "When bankflip give any failure response/message")
    );
  };
  // ********** clave functionality **********

  const handleInitiateUpload = () => {
    const claveDocs = [];
    documentList.map((docItem, index) => {
      if (docsData.CLAVE_DOCS.includes(selectedDocData?.[docItem])) {
        claveDocs.push(selectedDocData?.[docItem]);
      }
    });
    if (
      claveDocs.length > 0 &&
      user?.clave_done != true &&
      isFirstTime &&
      !isGuarantor
    ) {
      setShowApprovalPopup(true);
      setAvailableClaveDocs(claveDocs);
    } else {
      handleUploadFiles();
    }
  };

  const onhandleManualUpload = () => {
    setShowApprovalPopup(false);
    const url = new URL(window.location.href);
    url.search = ""; // ⛔️ Clear all query parameters
    window.history.pushState({}, "", url.toString());
    handleUploadFiles();
  };

  function removeWelcomeParam() {
    const url = new URL(window.location.href);

    if (url.searchParams.has("welcome")) {
      setShowWelcomePopup(false);
      url.searchParams.delete("welcome");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search
      );
    }
  }

  const removeValidFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeInvalidFile = (index) => {
    setInvalidFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const requiredDocumentCount = documentList.length;
  const identifiedDocumentCount = documentList.filter((requiredDocument) =>
    Array.from(docFound.entries()).some(
      ([key, document]) =>
        key === requiredDocument ||
        (docsMap[document?.type] ?? document?.type) === requiredDocument
    )
  ).length;
  const missingDocumentCount = Math.max(
    requiredDocumentCount - identifiedDocumentCount,
    0
  );
  const claveDocumentCount = documentList.filter((docItem) =>
    docsData.CLAVE_DOCS.includes(selectedDocData?.[docItem])
  ).length;
  const analysisReviewFiles = useMemo(() => {
    const files = buildFilesToReview(documentAnalysisResponse, user);
    // filter out KYC documents completely (passport, NIE, national identity, residence permit, identification bucket, etc.)
    return files.filter(
      (file) =>
        !isKycReviewFileUtil(file, tenantDocsData) &&
        (file.bucketName || "").toLowerCase() !== "identification"
    );
  }, [documentAnalysisResponse, user, tenantDocsData]);
  const reviewFiles = useMemo(() => {
    const tenantDocsUrlMap = new Map(
      tenantDocsData.map((doc) => [doc?.file_name, doc?.url])
    );
    const signedAnalysisFiles = analysisReviewFiles.map((file) => ({
      ...file,
      url: tenantDocsUrlMap.get(file.fileName) ?? null,
    }));
    const analysisFileNames = new Set(
      analysisReviewFiles.map((file) => file.fileName)
    );
    const extraFiles = otherDocs
      .filter((doc) => !analysisFileNames.has(doc?.file_name))
      .filter((doc) => !isKycTenantDoc(doc))
      .map((doc) => ({
        id: doc?._id || doc?.file_name,
        fileName: doc?.file_name,
        tag: "extra",
        documentId: doc?._id,
        url: doc?.url ?? tenantDocsUrlMap.get(doc?.file_name) ?? null,
      }));

    return [...signedAnalysisFiles, ...extraFiles].filter(
      (file) =>
        !deletedReviewFileIds.includes(file.id) &&
        !isKycReviewFileUtil(file, tenantDocsData) &&
        (file.bucketName || "").toLowerCase() !== "identification" &&
        !tenantDocsData.some(
          (doc) => doc?.from === "clave" && doc?.file_name === file.fileName
        )
    );
  }, [analysisReviewFiles, deletedReviewFileIds, otherDocs, tenantDocsData]);

  const isKycReviewFile = (file) => isKycReviewFileUtil(file, tenantDocsData);

  const handleDeleteReviewFile = async (file) => {
    if (isKycReviewFile(file)) return;
    try {
      setDeletingFileId(file.id);

      if (file.tag === "duplicate") {
        await deleteDuplicateDocuments({
          owner: file.ownerName,
          documentName: file.documentName,
        });
      } else {
        const matchedDocument = tenantDocsData.find(
          (doc) =>
            doc?._id === file.documentId || doc?.file_name === file.fileName
        );

        if (!matchedDocument?._id) {
          toast.error(_t("errorWhileDeletingDuplicateDoc"));
          return;
        }

        await deleteDocument(matchedDocument._id, user?._id);
      }

      setDeletedReviewFileIds((current) => [...current, file.id]);
      toast.success(_t("documentDeletedSuccessfully", "Document deleted"));
      await fetchFileData();
      loadUserTenant("tenant");
    } catch {
      toast.error(_t("errorWhileDeletingDuplicateDoc"));
    } finally {
      setDeletingFileId(null);
    }
  };

  const renderDocumentItem = (docItem, index, translationFromFixed = false) => {
    const docName = translationFromFixed
      ? docItem?.type
      : selectedDocData?.[docItem];
    if (!haveValue(docName) && !haveValue(docItem?.type)) return null;
    return (
      <DocItem
        key={docItem?._id || docItem?.file_name || docItem || index}
        docFound={docFound}
        docItem={
          translationFromFixed
            ? docItem
            : docFound.has(docItem)
              ? docFound.get(docItem)
              : (() => {
                  const match = Object.entries(docsMap).find(
                    ([key, value]) => value === docItem && docFound.has(key)
                  );
                  return match
                    ? docFound.get(match[0])
                    : payslipDocs.find((doc) => doc.type === docItem)
                      ? docItem
                      : null;
                })()
        }
        selectedDocData={selectedDocData}
        docsData={docsData}
        docName={
          translationFromFixed ? docItem?.type : selectedDocData?.[docItem]
        }
        translationFromFixed={translationFromFixed}
        handleInitiateClave={handleInitiateClave}
        index={index}
        haveClavePinDoc={haveClavePinDoc}
        showWelcomePopup={showWelcomePopup}
        hideClave={hideClave}
        payslipWarning={
          translationFromFixed && isThreePayslipRequired
            ? payslipValidation.get(docItem?._id || docItem?.file_name)
            : null
        }
      />
    );
  };

  return (
    <>
      <DisableZoom />
      <div
        className={["popup-overlay", "wrapper-modal", styles.overlay].join(" ")}
      >
        <div
          className={["required-doc-popup", styles.card].join(" ")}
          onClick={
            isGuarantor
              ? (event) => {
                  event.stopPropagation();
                  removeWelcomeParam();
                }
              : removeWelcomeParam
          }
        >
          {isKycDone && !cameFromMissing ? null : cameFromMissing &&
            typeof formHandler?.next === "function" &&
            !isEditMode &&
            !isGuarantor &&
            reqFor !== "updateDoc" ? (
            <button
              type="button"
              className={styles.backButton}
              onClick={() => {
                if (typeof formHandler?.goTo === "function")
                  formHandler.goTo(4);
                else formHandler.next();
              }}
            >
              <span aria-hidden="true">‹</span>
              {_t("goBack")}
            </button>
          ) : typeof formHandler?.prev === "function" ? (
            <button
              type="button"
              className={styles.backButton}
              onClick={() => formHandler.prev()}
            >
              <span aria-hidden="true">‹</span>
              {_t("back")}
            </button>
          ) : null}
          {(isGuarantor || isEditMode) && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => (onClose ? onClose() : navigate("/home"))}
              aria-label={_t("close")}
            >
              <img src={Cancelimg} alt="" />
            </button>
          )}
          <header className={styles.header}>
            <h1>{_t("requiredDocuments", "Your documents")}</h1>
            <p>
              {_t(
                "uploadMoreOrOtherIfYouDontHaveAll",
                "We need  documents to verify your solvency. Upload them straight from your computer, or use Cl@ve to import the ones available automatically. If any are missing, you can add them later."
              )}
            </p>
          </header>
          <div className={styles.contentGrid}>
            <section className={styles.requirements}>
              <div className={styles.countBadge}>
                {_t(
                  "requiredDocumentsCountBadge",
                  "Identified count badge",
                  "translation",
                  {
                    identified: identifiedDocumentCount,
                    required: requiredDocumentCount,
                  }
                )}
              </div>
              <div className={styles.documentList}>
                {documentLoading || waitingJobLoading ? (
                  <div className={styles.loader}>
                    <img
                      src={SkorDefaultLoader}
                      alt={_t("loading", "Loading")}
                    />
                  </div>
                ) : (
                  <>
                    <div className={styles.documentItems}>
                      {documentList.map((docItem, index) =>
                        renderDocumentItem(docItem, index)
                      )}
                      {payslipDocs.map((docItem, index) =>
                        renderDocumentItem(docItem, index, true)
                      )}
                      {additionalDocs.map((docItem, index) =>
                        renderDocumentItem(docItem, index, true)
                      )}
                    </div>
                    <p className={styles.instructions}>
                      {_t(
                        "dontKnowHowToFindTheseDocumentsFollowInstructionsFor"
                      )}
                      <a
                        href="/pdf/Docs_Spanish_Citizens.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {_t("spanishCitizen")}
                      </a>
                      {_t("orFor")}
                      <a
                        href="/pdf/Docs_Required_Non_Spanish_Citizens.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {_t("nonSpanishCitizen")}
                      </a>
                    </p>

                    {reviewFiles.length > 0 && (
                      <details className={styles.reviewSection} open>
                        <summary>
                          {_t(
                            "reviewFilesDetectedTitle",
                            "Other files detected title",
                            "translation",
                            { count: reviewFiles.length }
                          )}
                        </summary>
                        <p>
                          {_t(
                            "reviewFilesDetectedDescription",
                            "Other files detected description"
                          )}
                        </p>
                        <div className={styles.reviewList}>
                          {reviewFiles.map((file) => (
                            <div className={styles.reviewItem} key={file.id}>
                              {file.tag === "extra" ? (
                                <img
                                  className={styles.reviewIcon}
                                  src="/images/file-icon.svg"
                                  alt=""
                                />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={styles.reviewIcon}
                                  aria-hidden="true"
                                >
                                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                  <path d="M12 9v4" />
                                  <path d="M12 17h.01" />
                                </svg>
                              )}
                              {file.url ? (
                                <a
                                  className={styles.reviewFileName}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={file.fileName}
                                >
                                  {file.fileName}
                                </a>
                              ) : (
                                <span className={styles.reviewFileName}>
                                  {file.fileName}
                                </span>
                              )}
                              <span className={styles.reviewTag}>
                                {file.tag === "extra"
                                  ? _t("reviewTagExtra", "Extra file tag")
                                  : _t(
                                      REVIEW_TAGS[file.tag],
                                      "File review tag in onboarding"
                                    )}
                              </span>
                              {!isKycReviewFile(file) ? (
                                <button
                                  type="button"
                                  className={styles.deleteButton}
                                  onClick={() => handleDeleteReviewFile(file)}
                                  disabled={deletingFileId === file.id}
                                  aria-label={_t(
                                    "deleteDocument",
                                    "Delete document"
                                  )}
                                >
                                  <img src="/images/delete-icon.svg" alt="" />
                                </button>
                              ) : (
                                <div></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </div>
            </section>

            <section className={styles.uploadColumn}>
              {docUploadLoading ? (
                <div
                  className={[styles.uploadCard, styles.uploadLoader].join(" ")}
                >
                  <img src={SkorDefaultLoader} alt={_t("loading", "Loading")} />
                </div>
              ) : (
                <div
                  className={[
                    styles.uploadCard,
                    isDragging ? styles.dragging : "",
                  ].join(" ")}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className={styles.fileInput}
                    accept="application/pdf, image/jpeg, image/png, image/jpg"
                    multiple
                  />

                  <div className={styles.uploadIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 13v8" />
                      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                      <path d="m8 17 4-4 4 4" />
                    </svg>
                  </div>
                  <h2>{_t("dragAndDropYourDocumentsHere")}</h2>
                  <p className={styles.uploadHint}>
                    {_t(
                      "uploadHintDropSeveral",
                      "Upload hint for drag and drop"
                    )}
                  </p>

                  {(selectedFiles.length > 0 || invalidFiles.length > 0) && (
                    <div className={styles.uploadedList}>
                      {invalidFiles.map((file, index) => (
                        <div
                          key={file.name + index}
                          className={[
                            styles.uploadedFile,
                            styles.invalidFile,
                          ].join(" ")}
                        >
                          <div className={styles.fileName}>
                            <img src="/images/docIconGreen.svg" alt="" />
                            <span>{file.name}</span>
                          </div>
                          <span className={styles.fileError}>
                            {file.reason}
                          </span>
                          <button
                            type="button"
                            className={styles.removeFile}
                            onClick={() => removeInvalidFile(index)}
                            aria-label={_t("remove", "Remove")}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {selectedFiles.map((file, index) => {
                        const sizeInKB = file.size / 1024;
                        const sizeText =
                          sizeInKB < 1024
                            ? sizeInKB.toFixed(1) + " KB"
                            : (sizeInKB / 1024).toFixed(2) + " MB";

                        return (
                          <div
                            key={file.name + index}
                            className={styles.uploadedFile}
                          >
                            <div className={styles.fileName}>
                              <img src="/images/docIconGreen.svg" alt="" />
                              <span>{file.name}</span>
                            </div>
                            <span className={styles.fileSize}>{sizeText}</span>
                            <button
                              type="button"
                              className={styles.removeFile}
                              onClick={() => removeValidFile(index)}
                              aria-label={_t("remove", "Remove")}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.selectButton}
                    onClick={handleClick}
                  >
                    {selectedFiles.length > 0
                      ? _t("addMoreFiles", "Add more files")
                      : _t("selectFiles", "Select files button")}
                  </button>

                  {!hideClave &&
                    user?.employment_status !== "student" &&
                    user?.employment_status !== constants.company && (
                      <>
                        <button
                          type="button"
                          className={styles.claveButton}
                          onClick={handleInitiateClave}
                        >
                          {_t("importWithClave", "Import with Cl@ve button")}
                        </button>
                        <p className={styles.claveHint}>
                          <svg
                            className={styles.sparklesIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                            <path d="M20 2v4" />
                            <path d="M22 4h-4" />
                            <circle cx="4" cy="20" r="2" />
                          </svg>
                          {_t(
                            "claveFetchesDocumentsHint",
                            "Cl@ve fetches hint",
                            "translation",
                            {
                              claveCount: claveDocumentCount,
                              required: requiredDocumentCount,
                            }
                          )}
                        </p>
                      </>
                    )}
                </div>
              )}

              <p className={styles.reassurance}>
                <span aria-hidden="true">✓</span>
                {_t("reassuranceNoNeedToSort", "Reassurance text for upload")}
              </p>
            </section>
          </div>

          <div className={styles.actions}>
            {reqFor === "updateDoc" && (
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={onClose}
              >
                {_t("close")}
              </button>
            )}
            <div
              data-tooltip-id="button-action-tooltip"
              className={styles.actionTooltip}
            >
              <button
                type="button"
                onClick={handleInitiateUpload}
                className={styles.nextButton}
                disabled={
                  docUploadLoading ||
                  (docFound.size === 0 && selectedFiles.length === 0) ||
                  (user?.re_new_pending && selectedFiles.length === 0) ||
                  invalidFiles.length > 0
                }
              >
                {reqFor === "updateDoc" ? _t("submit") : _t("next")}
              </button>
              {docUploadLoading ||
                (docFound.size === 0 && selectedFiles.length === 0) ||
                (invalidFiles.length > 0 && <ButtonActionTooltip />)}
            </div>
          </div>
        </div>

        <ApprovedModal
          showPopup={showApprovalPopup}
          setShowApprovalPopup={setShowApprovalPopup}
          availableClaveDocs={availableClaveDocs}
          onhandleManualUpload={onhandleManualUpload}
          handleInitiateClave={handleInitiateClave}
        />
        {claveWidgetUrl && (
          <PermissoWidget
            widgetUrl={claveWidgetUrl}
            onClose={handleClose}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </div>
      {/* <DocumentAnalysisLoadingPopup
        isGuarantor={isGuarantor}
        showPopup={docUploadLoading}
      /> */}
    </>
  );
};

export default RequiredDocumentsModal;
